import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { VarietyService } from '../../../service/variety.service';
import { Variety } from '../../../models/Variety';
import { ModalEditVariety } from '../modal-edit-variety/modal-edit-variety';
import { Router } from '@angular/router';
import { exportRowsToExcel } from '../../../shared/utils/excel-export.util';
import { AgGridAngular } from 'ag-grid-angular';
import {
  AllCommunityModule,
  CellClickedEvent,
  ColDef,
  ICellRendererParams,
  ModuleRegistry
} from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-search-variety',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ModalEditVariety, AgGridAngular],
  templateUrl: './search-variety.html',
  styleUrl: './search-variety.scss'
})
export class SearchVariety {

  private fb = inject(FormBuilder);
  private varietyService = inject(VarietyService);
  private router = inject(Router);   // <-- CORRECTO

  searchForm = this.fb.group({
    term: [''],
    ineCode: ['']
  });

  loading = signal(false);
  varieties = signal<Variety[]>([]);
  error = signal<string | null>(null);
  hasSearched = signal(false);

  selectedVariety: Variety | null = null;
  readonly defaultColDef: ColDef<Variety> = {
    sortable: true,
    resizable: true,
    suppressMovable: true,
    filter: false,
    flex: 1,
    minWidth: 150
  };
  readonly columnDefs: ColDef<Variety>[] = [
    {
      headerName: 'Código INE',
      field: 'ineCode',
      minWidth: 190
    },
    {
      headerName: 'Nombre',
      field: 'name',
      minWidth: 280,
      sort: 'asc'
    },
    {
      headerName: 'Estado',
      field: 'status',
      minWidth: 130,
      maxWidth: 160,
      cellRenderer: (params: ICellRendererParams<Variety>) => {
        const active = params.value === 'A';
        return `<span class="fw-bold ${active ? 'text-success' : 'text-danger'}">` +
          `${active ? 'Activo' : 'Inactivo'}</span>`;
      }
    },
    {
      headerName: 'Acciones',
      colId: 'actions',
      sortable: false,
      filter: false,
      minWidth: 250,
      maxWidth: 270,
      cellRenderer: () => `
        <div class="variety-grid__actions">
          <button type="button" class="btn btn-sm btn-info text-white"
            data-action="edit" title="Editar variedad">
            <i class="bi bi-pencil-square me-1"></i> Editar
          </button>
          <button type="button" class="btn btn-sm btn-outline-info"
            data-action="articles" title="Consultar artículos de la variedad">
            <i class="bi bi-diagram-3 me-1"></i> Artículos
          </button>
        </div>`
    }
  ];
  readonly localeText = {
    noRowsToShow: 'No hay variedades para mostrar',
    page: 'Página',
    to: 'a',
    of: 'de',
    next: 'Siguiente',
    last: 'Última',
    first: 'Primera',
    previous: 'Anterior',
    loadingOoo: 'Cargando...'
  };

  // Buscar
  onSearch() {
    const nombre = this.searchForm.value.term?.trim().toLocaleLowerCase('es') || '';
    const codigoIne = this.searchForm.value.ineCode?.trim().toLocaleLowerCase('es') || '';

    if (!nombre && !codigoIne) {
      this.error.set('Ingrese un nombre o código INE para buscar.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.hasSearched.set(true);

    this.varietyService.getAll().subscribe({
      next: data => {
        this.loading.set(false);
        this.varieties.set(
          data.filter(v =>
            (!nombre || v.name?.toLocaleLowerCase('es').includes(nombre)) &&
            (!codigoIne || v.ineCode?.toLocaleLowerCase('es').includes(codigoIne))
          )
        );
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Error al buscar variedades.');
      }
    });
  }

  // Listar todo
  onListAll() {
    this.loading.set(true);
    this.error.set(null);
    this.hasSearched.set(true);

    this.varietyService.getAll().subscribe({
      next: data => {
        this.varieties.set(data);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Error al consultar todas las variedades.');
      }
    });
  }

  // Abrir modal de edición
  openEdit(v: Variety) {
    this.selectedVariety = v;

    const modal = new (window as any).bootstrap.Modal(
      document.getElementById('modalEditVariety')
    );
    modal.show();
  }

  // Eliminar variedad desde modal
  onVarietyDeleted(codigo: number) {
    this.varieties.update(list => list.filter(v => v.id !== codigo));
    this.selectedVariety = null;
  }

  // Actualizar registro en la tabla después de editar
  onVarietyUpdated(updated: Variety) {
    this.varieties.update(list =>
      list.map(v => (v.id === updated.id ? updated : v))
    );
  }

  // Ver artículos de esta variedad
  viewArticles(codigo: number) {
    this.router.navigate(['/variety', codigo, 'articles']);
  }

  onCellClicked(event: CellClickedEvent<Variety>): void {
    if (event.colDef.colId !== 'actions' || !event.data) return;
    const action = (event.event?.target as HTMLElement | null)
      ?.closest<HTMLElement>('[data-action]')?.dataset['action'];

    if (action === 'edit') this.openEdit(event.data);
    if (action === 'articles') this.viewArticles(event.data.id ?? 0);
  }

  exportExcel(): void {
    exportRowsToExcel(this.varieties().map(v => ({
      'Código': v.id,
      'Código de sección': v.sectionId,
      'Código de división': v.divisionId,
      'Código de clase': v.classId,
      'Código de producto': v.productId,
      'Código de unidad de medida': v.measurementUnitId,
      'Cantidad base': v.baseQuantity,
      'Código INE': v.ineCode,
      'Nombre': v.name,
      'Código CIIU': v.ciiuCode,
      'Código NAEG': v.naegCode,
      'Código NPG': v.npgCode,
      'Código CCP': v.ccpCode,
      'Referencia': v.reference,
      'Estado': v.status === 'A' ? 'Activo' : 'Inactivo',
      'Usuario de auditoría': v.auditUserId,
      'Transacción de auditoría': v.auditTransactionId
    })), 'mantenimiento-variedades', 'Variedades');
  }
}
